import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Clock, MessageCircle, CalendarCheck, Send, Instagram } from "lucide-react";
import { motion } from "framer-motion";
import { api } from "../lib/api";
import { PageHero } from "./About";
import { useClinic, waLink } from "../context/ClinicContext";
import { ScrollReveal } from "../components/ScrollReveal";

const empty = { full_name: "", phone: "", email: "", service: "", preferred_date: "", preferred_time: "", message: "" };

export default function Contact() {
  const c = useClinic();
  const { data: services = [] } = useQuery({ queryKey: ["services"], queryFn: async () => (await api.get("/services")).data });
  const [form, setForm] = useState(empty);
  const [submitting, setSubmitting] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.phone) { toast.error("Please enter your name and phone number."); return; }
    setSubmitting(true);
    try {
      await api.post("/appointments", form);
      toast.success("Appointment request sent! We'll contact you shortly.");
      const waText = `Hello Revive Family Clinic, I'd like to book an appointment.%0AName: ${form.full_name}%0APhone: ${form.phone}%0AService: ${form.service}%0ADate: ${form.preferred_date} ${form.preferred_time}`;
      window.open(`https://wa.me/${(c.whatsapp || "").replace(/[^0-9]/g, "")}?text=${waText}`, "_blank");
      setForm(empty);
    } catch {
      toast.error("Something went wrong. Please try again or call us.");
    } finally { setSubmitting(false); }
  };

  const inputCls = "w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#1A75BC]/40 focus:border-[#1A75BC] transition";

  return (
    <div>
      <PageHero eyebrow="Contact" title="Book an Appointment" subtitle="Request an appointment and our team will confirm your slot as soon as possible." />
      <section className="py-14 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 grid lg:grid-cols-5 gap-10">
          <ScrollReveal className="lg:col-span-3" id="book-appointment">
            <form onSubmit={submit} className="bg-white rounded-3xl border border-slate-100 shadow-sm p-6 sm:p-8 space-y-4" data-testid="appointment-form">
              <h2 className="text-xl font-extrabold text-[#0F1E36] flex items-center gap-2"><CalendarCheck className="text-[#1A75BC]" size={22} /> Request Appointment</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <div><label className="text-sm font-semibold text-[#334E68]">Full Name *</label><input className={inputCls + " mt-1"} value={form.full_name} onChange={set("full_name")} data-testid="appt-name" placeholder="Your full name" /></div>
                <div><label className="text-sm font-semibold text-[#334E68]">Phone Number *</label><input className={inputCls + " mt-1"} value={form.phone} onChange={set("phone")} data-testid="appt-phone" placeholder="+91 ..." /></div>
              </div>
              <div><label className="text-sm font-semibold text-[#334E68]">Email Address</label><input type="email" className={inputCls + " mt-1"} value={form.email} onChange={set("email")} data-testid="appt-email" placeholder="you@email.com" /></div>
              <div><label className="text-sm font-semibold text-[#334E68]">Department / Service</label>
                <select className={inputCls + " mt-1 bg-white"} value={form.service} onChange={set("service")} data-testid="appt-service">
                  <option value="">Select a service</option>
                  {services.map((s) => <option key={s.id} value={s.title}>{s.title}</option>)}
                </select>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div><label className="text-sm font-semibold text-[#334E68]">Preferred Date</label><input type="date" className={inputCls + " mt-1"} value={form.preferred_date} onChange={set("preferred_date")} data-testid="appt-date" /></div>
                <div><label className="text-sm font-semibold text-[#334E68]">Preferred Time</label><input type="time" className={inputCls + " mt-1"} value={form.preferred_time} onChange={set("preferred_time")} data-testid="appt-time" /></div>
              </div>
              <div><label className="text-sm font-semibold text-[#334E68]">Message</label><textarea rows={3} className={inputCls + " mt-1 resize-none"} value={form.message} onChange={set("message")} data-testid="appt-message" placeholder="Tell us briefly about your concern" /></div>
              <button type="submit" disabled={submitting} className="w-full gradient-bg btn-glow text-white font-bold py-3.5 rounded-full flex items-center justify-center gap-2 disabled:opacity-60" data-testid="appt-submit">
                <Send size={18} /> {submitting ? "Sending..." : "Request Appointment"}
              </button>
            </form>
          </ScrollReveal>

          <ScrollReveal delay={0.12} className="lg:col-span-2 space-y-4">
            <motion.div animate={{ y: [0, -10, 0] }} transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }} className="glass-tint rounded-3xl p-6 sm:p-7 shadow-xl relative overflow-hidden" data-testid="get-in-touch">
              <div className="absolute -top-16 -right-12 w-40 h-40 rounded-full bg-[#2BB673]/25 blur-2xl pointer-events-none" />
              <div className="absolute -bottom-16 -left-12 w-40 h-40 rounded-full bg-[#1A75BC]/25 blur-2xl pointer-events-none" />
              <h3 className="font-extrabold text-lg text-[#0F1E36] relative mb-4">Get in Touch</h3>
              <div className="relative space-y-2.5">
                {[
                  [Phone, "Phone", c.phone, `tel:${c.phone}`],
                  [MessageCircle, "WhatsApp", "Chat with us", waLink(c.whatsapp)],
                  [Mail, "Email", c.email, `mailto:${c.email}`],
                  [Instagram, "Instagram", "@revivefamilyclinic", c.instagram],
                  [MapPin, "Address", c.address, c.map_url],
                  [Clock, "Opening Hours", c.hours, null],
                ].map(([Icon, label, val, href], i) => {
                  const inner = (
                    <>
                      <div className="w-10 h-10 rounded-xl gradient-bg flex items-center justify-center shrink-0 text-white shadow-md"><Icon size={18} /></div>
                      <div className="min-w-0"><div className="text-[11px] uppercase tracking-wide text-[#5B7591] font-bold">{label}</div><div className="text-sm text-[#0F1E36] font-medium break-words whitespace-pre-line">{val}</div></div>
                    </>
                  );
                  return href ? (
                    <a key={i} href={href} target="_blank" rel="noreferrer" data-testid={`contact-${label.toLowerCase()}`} className="flex gap-3 items-center bg-white/55 hover:bg-white/85 border border-white/60 rounded-2xl p-3 transition-colors">{inner}</a>
                  ) : (
                    <div key={i} className="flex gap-3 items-center bg-white/45 border border-white/60 rounded-2xl p-3">{inner}</div>
                  );
                })}
              </div>
            </motion.div>
            {c.map_embed && (
              <div className="rounded-3xl overflow-hidden border border-white/60 h-60 shadow-lg">
                <iframe title="Clinic Location" src={c.map_embed} className="w-full h-full border-0" loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
              </div>
            )}
            {c.map_url && (
              <a href={c.map_url} target="_blank" rel="noreferrer" data-testid="open-google-maps" className="flex items-center justify-center gap-2 glass text-[#1A75BC] font-bold text-sm px-5 py-3 rounded-full hover:bg-white/80 transition-colors">
                <MapPin size={16} /> Open in Google Maps
              </a>
            )}
          </ScrollReveal>
        </div>
      </section>
    </div>
  );
}
